// src/pages/MyFlyersPage.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";

function getStatusColour(status) {
  const normalisedStatus = String(status || "").toLowerCase();

  if (normalisedStatus === "active") return "green";
  if (normalisedStatus === "draft") return "yellow";
  if (normalisedStatus === "paused") return "orange";
  if (normalisedStatus === "completed") return "blue";

  return "gray";
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return value;
}

export default function MyFlyersPage() {
  const { authId, loading } = useBusinessGuard();

  const [flyers, setFlyers] = useState([]);
  const [campaignLookup, setCampaignLookup] = useState({});
  const [loadingFlyers, setLoadingFlyers] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    if (!authId) {
      setFlyers([]);
      setCampaignLookup({});
      setLoadingFlyers(false);
      return undefined;
    }

    async function loadFlyers() {
      setLoadingFlyers(true);
      setLoadError("");

      // 1. Resolve the current business's integer primary key.
      const { data: business, error: businessError } = await supabase
        .from("businesses")
        .select("id")
        .eq("auth_id", authId)
        .maybeSingle();

      if (cancelled) return;

      if (businessError) {
        console.error("[MyFlyersPage] business lookup error:", businessError);
        setFlyers([]);
        setCampaignLookup({});
        setLoadError("Unable to load the business account.");
        setLoadingFlyers(false);
        return;
      }

      if (!business?.id) {
        setFlyers([]);
        setCampaignLookup({});
        setLoadError("No business profile is linked to this account.");
        setLoadingFlyers(false);
        return;
      }

      // 2. Load only campaigns owned by the current business.
      const { data: campaigns, error: campaignsError } = await supabase
        .from("campaigns")
        .select("id, name")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false });

      if (cancelled) return;

      if (campaignsError) {
        console.error("[MyFlyersPage] campaign load error:", campaignsError);
        setFlyers([]);
        setCampaignLookup({});
        setLoadError("Unable to load your campaigns.");
        setLoadingFlyers(false);
        return;
      }

      const ownedCampaigns = campaigns || [];
      const campaignIds = ownedCampaigns.map((campaign) => campaign.id);
      const nextCampaignLookup = Object.fromEntries(
        ownedCampaigns.map((campaign) => [campaign.id, campaign.name])
      );

      setCampaignLookup(nextCampaignLookup);

      // A business without campaigns cannot own campaign flyers.
      if (campaignIds.length === 0) {
        setFlyers([]);
        setLoadingFlyers(false);
        return;
      }

      // 3. Load only flyers whose campaign_id belongs to this business.
      const { data: ownedFlyers, error: flyersError } = await supabase
        .from("flyers")
        .select(
          "id, campaign_id, postcode_sector, season, year, status, created_at"
        )
        .in("campaign_id", campaignIds)
        .order("created_at", { ascending: false });

      if (cancelled) return;

      if (flyersError) {
        console.error("[MyFlyersPage] flyer load error:", flyersError);
        setFlyers([]);
        setLoadError("Unable to load your flyers.");
      } else {
        setFlyers(ownedFlyers || []);
      }

      setLoadingFlyers(false);
    }

    loadFlyers();

    return () => {
      cancelled = true;
    };
  }, [authId]);

  const flyerCountLabel = useMemo(() => {
    const count = flyers.length;
    return `${count} ${count === 1 ? "flyer" : "flyers"}`;
  }, [flyers.length]);

  if (loading) {
    return (
      <Flex p={8} align="center" gap={3}>
        <Spinner size="sm" color="red.500" />
        <Text>Loading account…</Text>
      </Flex>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }} width="100%" overflowX="hidden">
      <Flex
        mb={6}
        gap={3}
        align={{ base: "flex-start", sm: "center" }}
        justify="space-between"
        direction={{ base: "column", sm: "row" }}
      >
        <Box>
          <Heading size="lg" mb={2}>
            My Flyers
          </Heading>
          <Text color="gray.600">
            Flyers belonging to campaigns owned by your business.
          </Text>
        </Box>

        {!loadingFlyers && !loadError && (
          <Badge colorScheme="red" px={3} py={1} borderRadius="full">
            {flyerCountLabel}
          </Badge>
        )}
      </Flex>

      {loadingFlyers ? (
        <Flex align="center" gap={3} py={8}>
          <Spinner size="sm" color="red.500" />
          <Text>Loading flyers…</Text>
        </Flex>
      ) : loadError ? (
        <Box
          borderWidth="1px"
          borderColor="red.200"
          bg="red.50"
          color="red.700"
          borderRadius="md"
          p={4}
        >
          {loadError}
        </Box>
      ) : flyers.length === 0 ? (
        <Box
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
          borderRadius="lg"
          p={6}
        >
          <Heading size="sm" mb={2}>
            No flyers yet
          </Heading>
          <Text color="gray.600">
            Flyers will appear here after a campaign is created for your
            business.
          </Text>
        </Box>
      ) : (
        <>
          {/* Mobile and tablet card layout */}
          <SimpleGrid
            display={{ base: "grid", lg: "none" }}
            columns={{ base: 1, sm: 2 }}
            spacing={4}
          >
            {flyers.map((flyer) => (
              <Box
                key={flyer.id}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                bg="white"
                p={4}
                boxShadow="sm"
                minW={0}
              >
                <Flex justify="space-between" align="flex-start" gap={3} mb={4}>
                  <Box minW={0}>
                    <Text
                      fontSize="lg"
                      fontWeight="extrabold"
                      color="red.600"
                      noOfLines={1}
                    >
                      {formatValue(flyer.postcode_sector)}
                    </Text>
                    <Text fontSize="sm" color="gray.600" noOfLines={1}>
                      {formatValue(campaignLookup[flyer.campaign_id])}
                    </Text>
                  </Box>

                  <Badge
                    flexShrink={0}
                    colorScheme={getStatusColour(flyer.status)}
                    textTransform="capitalize"
                  >
                    {formatValue(flyer.status)}
                  </Badge>
                </Flex>

                <SimpleGrid columns={2} spacing={3} mb={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                      Season
                    </Text>
                    <Text fontWeight="semibold">
                      {formatValue(flyer.season)}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                      Year
                    </Text>
                    <Text fontWeight="semibold">
                      {formatValue(flyer.year)}
                    </Text>
                  </Box>
                </SimpleGrid>

                <Flex gap={2} wrap="wrap">
                  <Button
                    as={RouterLink}
                    to={`/flyer/${flyer.id}/details`}
                    size="sm"
                    variant="outline"
                    flex="1"
                    minW="100px"
                  >
                    Details
                  </Button>

                  <Button
                    as={RouterLink}
                    to={`/flyer/${flyer.id}/preview`}
                    size="sm"
                    colorScheme="red"
                    flex="1"
                    minW="100px"
                  >
                    Preview
                  </Button>
                </Flex>
              </Box>
            ))}
          </SimpleGrid>

          {/* Desktop table layout */}
          <Box
            display={{ base: "none", lg: "block" }}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="lg"
            bg="white"
            overflowX="auto"
          >
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Postcode sector</Th>
                  <Th>Season</Th>
                  <Th>Status</Th>
                  <Th>Campaign</Th>
                  <Th isNumeric>Year</Th>
                  <Th textAlign="right">Actions</Th>
                </Tr>
              </Thead>

              <Tbody>
                {flyers.map((flyer) => (
                  <Tr key={flyer.id}>
                    <Td fontWeight="semibold">
                      {formatValue(flyer.postcode_sector)}
                    </Td>
                    <Td>{formatValue(flyer.season)}</Td>
                    <Td>
                      <Badge
                        colorScheme={getStatusColour(flyer.status)}
                        textTransform="capitalize"
                      >
                        {formatValue(flyer.status)}
                      </Badge>
                    </Td>
                    <Td>{formatValue(campaignLookup[flyer.campaign_id])}</Td>
                    <Td isNumeric>{formatValue(flyer.year)}</Td>
                    <Td>
                      <Flex justify="flex-end" gap={2}>
                        <Button
                          as={RouterLink}
                          to={`/flyer/${flyer.id}/details`}
                          size="xs"
                          variant="outline"
                        >
                          Details
                        </Button>
                        <Button
                          as={RouterLink}
                          to={`/flyer/${flyer.id}/preview`}
                          size="xs"
                          colorScheme="red"
                        >
                          Preview
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}
